
/**
 * Module dependencies.
 */

import { ImageSources, LocalImageSources, Text, typography, useImageSources } from 'slyk-web-libs/design-system';
import { Product as ProductType, VideoSource } from 'slyk-web-libs/types';
import { camelCase } from 'lodash';
import { color } from 'react-components/styles';
import { getImageSources } from 'client/core/utils/image';
import { ifProp, theme } from 'styled-tools';
import { routeTemplate } from 'client/core/utils/routes';
import { unitsOfAccount } from 'client/core/constants/product';
import { useHistory } from 'react-router-dom';
import { useTranslate } from 'slyk-web-libs/i18n';
import React, { useCallback, useState } from 'react';
import payspaceRoutes from 'slyk-web-libs/config/payspace-routes';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  product: ProductType & {
    displayPrice: string;
    displayPriceConverted: string | null;
  };
};

/**
 * Unit of account types.
 */

const unitOfAccountTypes = Object.keys(unitsOfAccount);

/**
 * `ProductWrapper` styled component.
 */

const ProductWrapper = styled.div`
  position: relative;
`;

/**
 * `ProductContent` styled component.
 */

const ProductContent = styled.div<{ isUnavailable: boolean }>`
  position: relative;

  ${ifProp('isUnavailable', css`
    opacity: 0.5;
  `)}
`;

/**
 * `Tag` styled component.
 */

const Tag = styled.div`
  ${typography.styles.captionExtraSmall}

  background-color: ${color('gray200')};
  border-radius: 6px;
  color: ${color('gray800')};
  display: flex;
  font-weight: 500;
  justify-content: center;
  padding: 4px 6px;
  position: absolute;
  right: 8px;
  text-transform: uppercase;
  top: 8px;
`;

/**
 * `ProductImageButton` styled component.
 */

const ProductImageButton = styled.button`
  appearance: none;
  border: none;
  border-radius: 6px;
  box-shadow: 0 1px 3px 0 rgba(25, 28, 30, 0.07);
  cursor: pointer;
  display: flex;
  outline: none;
  padding: 0;
  transition: box-shadow ${theme('animations.defaultTransition')};
  width: 100%;

  &:hover,
  &:focus {
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.22);
  }
`;

/**
 * `ProductImage` styled component.
 */

const ProductImage = styled.img`
  aspect-ratio: 3 / 4;
  border-radius: inherit;
  display: block;
  height: auto;
  object-fit: cover;
  width: 100%;
`;

/**
 * `ProductInformation` styled component.
 */

const ProductInformation = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

/**
 * `Name` styled component.
 */

const Name = styled(Text).attrs({ size: 'normal' })`
  color: ${color('secondary')};
  font-weight: 700;
  margin-bottom: 8px;
  word-break: break-word;
`;

/**
 * `ProductPrice` styled component.
 */

const ProductPrice = styled(Text).attrs({ size: 'normal' })`
  color: ${color('nightBlue900')};
  word-break: break-word;
`;

/**
 * `AdditionalInfo` styled component.
 */

const AdditionalInfo = styled.span`
  margin-left: 3px;
`;

/**
 * `PriceConverted` styled component.
 */

const PriceConverted = styled(Text).attrs({ size: 'small' })`
  color: ${color('gray500')};
`;

/**
 * Export `ProductItem` component.
 */

export function ProductItem({ product }: Props): JSX.Element {
  const { available, displayPrice, displayPriceConverted, id, location, name, thumbnail, unitOfAccount } = product;
  const { translate } = useTranslate();
  const history = useHistory();
  const isUnavailable = !available;
  const imageSources = useImageSources((thumbnail as VideoSource)?.thumbnailUrl ?? thumbnail?.url, '128x');
  const [imageSource, setImageSource] = useState<ImageSources | null>(imageSources);
  const noImageSources = getImageSources('no-image/product', 'webp') as LocalImageSources;
  const handleProductDetails = useCallback(() => {
    return history.push(routeTemplate(payspaceRoutes.products.details, { id }));
  }, [history, id]);

  const handleError = useCallback(() => {
    setImageSource(noImageSources);
  }, [noImageSources]);

  return (
    <ProductWrapper>
      <ProductContent isUnavailable={isUnavailable}>
        <ProductImageButton
          onClick={handleProductDetails}
          type={'button'}
        >
          <ProductImage
            alt={''}
            onError={handleError}
            src={imageSource?.defaultUrl ?? noImageSources.defaultUrl}
            srcSet={imageSource?.retinaUrl ?? noImageSources.retinaUrl}
          />
        </ProductImageButton>

        <ProductInformation>
          <Name>
            {name}

            {!!location && (
              <AdditionalInfo>
                {translate([`products.location.${camelCase(location)}`, ''])}
              </AdditionalInfo>
            )}
          </Name>

          <ProductPrice>
            {displayPrice}

            {!!unitOfAccount && (
              <AdditionalInfo>
                {unitOfAccountTypes.includes(unitOfAccount) ? translate('products.per', {
                  unitOfAccount: translate(`products.unitOfAccount.${unitOfAccount}`)
                }) : translate('products.per', { unitOfAccount })}
              </AdditionalInfo>
            )}
          </ProductPrice>

          {!!displayPriceConverted && (
            <PriceConverted>
              {displayPriceConverted}
            </PriceConverted>
          )}
        </ProductInformation>
      </ProductContent>

      {isUnavailable && (
        <Tag>
          {translate('products.unavailable')}
        </Tag>
      )}
    </ProductWrapper>
  );
}
